import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faHandshakeSimple } from '@fortawesome/free-solid-svg-icons';
//import Footer from './Footer';
import './Home.css';

/* ─── Animation variants ─────────────────────────────────────── */
var fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show:   { opacity: 1, y: 0,  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

var stagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.13 } },
};

var scaleIn = {
  hidden: { opacity: 0, scale: 0.94 },
  show:   { opacity: 1, scale: 1, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
};

/* ─── Project data ───────────────────────────────────────────── */
var PROJECTS = [
  { src: '/jacob.jpeg',   alt: 'Portfolio website',       caption: 'Personal Portfolio — React JS + NestJs' },
  { src: '/zolu.png',     alt: 'Web application project', caption: 'Built a scalable full-stack platform with secure APIs and optimized database performance' },
  { src: '/business.png', alt: 'study-site',              caption: 'IMBI — React JS + Laravel + PostgreSQL' },
];

/* ─── Component ──────────────────────────────────────────────── */
var Home = function() {
  var greetingState = useState('');
  var greeting      = greetingState[0];
  var setGreeting   = greetingState[1];

  useEffect(function() {
    var hour = new Date().getHours();
    if (hour < 12)      setGreeting('Good Morning');
    else if (hour < 17) setGreeting('Good Afternoon');
    else                setGreeting('Good Evening');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <motion.div
      className="home-root"
      initial="hidden"
      animate="show"
      variants={stagger}
    >

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="home-hero" aria-label="Hero section">
        <div className="home-hero__noise" aria-hidden="true" />
        <div className="home-hero__glow home-hero__glow--1" aria-hidden="true" />
        <div className="home-hero__glow home-hero__glow--2" aria-hidden="true" />

        <Container className="home-hero__inner">
          <div className="home-hero__layout">

            {/* ── Left: text content ──────────────────── */}
            <div className="home-hero__content">
              <motion.span className="home-hero__greeting" variants={fadeUp}>
                {greeting}
              </motion.span>

              <motion.h1 className="home-hero__title" variants={fadeUp}>
                I build secure,<br />
                <em>scalable APIs</em> and <em>high-performance web applications.</em>
              </motion.h1>

              <motion.p className="home-hero__sub" variants={fadeUp}>
               I'm <strong>Jacob Chidi Eugene,</strong> a Senior Software Engineer specializing in backend systems, API security, and AI-powered applications.
              </motion.p>
              <motion.p className="home-hero__value" variants={fadeUp}>
               I help startups and businesses build reliable, secure systems that scale; and avoid costly security mistakes.
              </motion.p>

              <motion.div className="home-hero__actions" variants={fadeUp}>
                <Link to="/about" className="home-btn home-btn--primary">
                  About Me <FontAwesomeIcon icon={faArrowRight} />
                </Link>
                <Link to="/contact" className="home-btn home-btn--outline">
                 Hire Me for API & Backend Projects <FontAwesomeIcon icon={faHandshakeSimple} />
                </Link>
              </motion.div>

              {/* Stats row */}
              <motion.div className="home-hero__stats" variants={fadeUp}>
                {[
                  { value: '5+',  label: 'Years Building Production Systems'  },
                  { value: '10+', label: 'Projects Delivered' },
                  { value: '20+', label: 'Happy Clients & Students'      },
                ].map(function(s) {
                  return (
                    <div className="home-stat" key={s.label}>
                      <span className="home-stat__value">{s.value}</span>
                      <span className="home-stat__label">{s.label}</span>
                    </div>
                  );
                })}
              </motion.div>
            </div>

            {/* ── Right: portrait ─────────────────────── */}
            <motion.div className="home-hero__portrait-wrap" variants={fadeUp}>
              <div className="home-hero__portrait-ring" aria-hidden="true" />
              <div className="home-hero__portrait-glow" aria-hidden="true" />
              <img
                src="/jacob.jpeg"
                alt="Jacob Chidi Eugene — Senior Software Engineer"
                className="home-hero__portrait"
                width="420"
                height="520"
                loading="eager"
              />
              <div className="home-hero__badge" aria-label="Available for hire">
                <span className="home-hero__badge-dot" aria-hidden="true" />
                Available for hire
              </div>
            </motion.div>

          </div>
        </Container>

        {/* Scroll cue */}
        <div className="home-hero__scroll" aria-hidden="true">
          <span className="home-hero__scroll-line" />
        </div>
      </section>

      {/* ── Video Introduction ───────────────────────────────── */}
      <section className="home-section" aria-labelledby="video-heading">
        <Container>
          <motion.div className="home-section__header" variants={fadeUp}>
            <span className="home-label">Watch</span>
            <h2 id="video-heading" className="home-section__title">Video Introduction</h2>
            <p className="home-section__sub">
              See how I approach building scalable and secure applications in real-world projects.
            </p>
          </motion.div>

          <motion.div className="home-video-wrap" variants={scaleIn}>
            <video
              controls
              className="home-video"
              aria-label="Jacob Chidi Eugene video introduction"
              preload="metadata"
            >
              <source src="/videos/presentation.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </motion.div>
        </Container>
      </section>

      {/* ── Projects Showcase ────────────────────────────────── */}
      <section className="home-section home-section--alt" aria-labelledby="projects-heading">
        <Container>
          <motion.div className="home-section__header" variants={fadeUp}>
            <span className="home-label">Work</span>
            <h2 id="projects-heading" className="home-section__title">Projects I've Built</h2>
            <p className="home-section__sub">
              A selection of applications and experiences crafted with care.
            </p>
          </motion.div>

          <motion.div className="home-grid" variants={stagger}>
            {PROJECTS.map(function(p, i) {
              return (
                <motion.article
                  key={i}
                  className="home-project-card"
                  variants={scaleIn}
                  aria-label={p.alt}
                >
                  <div className="home-project-card__img-wrap">
                    <img src={p.src} alt={p.alt} loading="lazy" />
                    <div className="home-project-card__overlay" aria-hidden="true" />
                  </div>
                  <p className="home-project-card__caption">{p.caption}</p>
                </motion.article>
              );
            })}
          </motion.div>

          <motion.div className="home-section__cta" variants={fadeUp}>
            <Link to="/projects" className="home-btn home-btn--outline">
              View All Projects <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </motion.div>
        </Container>
        <section className="home-section home-section--cta">
  <div className="home-section__header">
    <span className="home-label">Contact</span>
    <h2 className="home-section__title">Let’s Work Together</h2>
    <p className="home-section__sub">
      Need help building secure APIs or scalable systems? I’m available for freelance and consulting work.
    </p>
  </div>

  <div className="home-section__cta">
    <Link to="/contact" className="home-btn home-btn--primary">
      Get in Touch
    </Link>
  </div>
</section>
      </section>
    </motion.div>
  );
};

export default Home;